// web/src/events/EventCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, Share2, Trash2, Pencil, Send, Users } from "lucide-react";
import { addFavorite, removeFavorite, toggleFavorite } from "@/events/favorites";
import { setRsvp, toggleRsvp } from "@/events/rsvp";
import { subscribeRsvpCount, subscribeRsvps } from "@/events/api";
import { eventFallbackByVisibility } from "@/lib/images";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot, getDoc, collection, addDoc, query, where, getDocs } from "firebase/firestore";

// Normalize potentially bad Firebase Storage URLs
function fixUrl(u?: string) {
  if (!u || typeof u !== "string") return "";
  // Do NOT strip query params (token needed for Firebase Storage)
  // Normalize legacy ?name= form (cannot restore token, so prefer original if token already present)
  const legacy = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\?name=([^&]+)$/.exec(u);
  if (legacy) {
    // Fallback without token (works only if object is public); prefer real downloadURL when available
    return `https://firebasestorage.googleapis.com/v0/b/${legacy[1]}/o/${legacy[2]}?alt=media`;
  }
  return u;
}

function coerceDate(val: any): Date | null {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (val?.toDate) {
    try { const d = val.toDate(); return d instanceof Date && !isNaN(d.getTime()) ? d : null; } catch {}
  }
  if (val?.toMillis) {
    try { const d = new Date(val.toMillis()); return !isNaN(d.getTime()) ? d : null; } catch {}
  }
  if (typeof val === "number") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "string") {
    const t = Date.parse(val);
    if (Number.isFinite(t)) {
      const d = new Date(t);
      if (!isNaN(d.getTime())) return d;
    }
  }
  return null;
}
function safeFormat(val: any, pattern: string, fallback = "TBD") {
  const d = coerceDate(val);
  if (!d) return fallback;
  try { return format(d, pattern); } catch {
    console.warn("[EventCard] invalid date encountered", val);
    return fallback;
  }
}

export type EventDoc = {
  id: string;
  title: string;
  details: string;
  formatTypes: string[];
  visibility: "public" | "private";
  startTime: any;           // was Date; allow mixed types
  endTime?: any | null;
  organizerUid: string;
  organizerUsername: string;
  organizerDisplayName?: string | null;
  organizerAvatarUrl?: string | null;
  organizerIsLGS?: boolean;
  images?: string[];
  distanceMiles?: number | null;
  favoritesCount?: number;
  eventType?: string | null;
  address?: string | null;
  locationType?: string | null;
  location?: { lat: number; lng: number; geohash?: string; name?: string | null } | null;
  lgsEmail?: string | null; // OPTIONAL: email for LGS organizer (if available)
  organizerEmail?: string | null; // fallback if provided
};

type Props = {
  ev: EventDoc;
  isFavorite?: boolean;
  // NEW manage capabilities
  canManage?: boolean;
  onEdit?: (ev: EventDoc) => void;
  onDelete?: (ev: EventDoc) => void;
};

function googleMapsHref(address?: string | null, loc?: { lat?: number; lng?: number } | null) {
  if (loc?.lat != null && loc?.lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${loc.lat},${loc.lng}`)}`;
  }
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return "https://maps.google.com";
}

function pickHero(ev: EventDoc): string {
  const vis = ev.visibility === "private" ? "private" : "public";
  const visFallback = vis === "public" ? "/events/public.png" : "/events/private.png";
  const isLgs = !!ev.organizerIsLGS || (ev as any).locationType === "LGS";

  // Is this a custom, user-selected image? (not seeded/fallback)
  const isCustom = (u?: string | null) => {
    if (!u) return false;
    const s = (u || "").toLowerCase();
    if (s.endsWith("/events/public.png") || s.endsWith("/events/private.png") || s.endsWith("/events/placeholder.png")) return false;
    if (s.includes("/events/seeded-venues/")) return false;
    return true;
  };

  const imgs = Array.isArray(ev.images) ? ev.images : [];
  const firstCustom = imgs.map((x) => fixUrl(typeof x === "string" ? x : (x as any)?.src || (x as any)?.url)).find(isCustom);

  // Priority:
  // 1) If event has a custom image (non-seeded), use it
  if (firstCustom) return firstCustom;

  // 2) If organizer is LGS, prefer the LGS avatar
  if (isLgs) {
    return fixUrl(ev.organizerAvatarUrl || undefined) || visFallback;
  }

  // 3) Otherwise, use first image if provided, else fallback
  const first = imgs.length ? fixUrl(imgs[0] || undefined) : null;
  return first || visFallback;
}

// Strengthen contrast on the card container and text.
export default function EventCard({ ev, onSwipeLeft, onSwipeRight, onFavoriteClick, onShareClick, isFavorite, onEdit, onDelete, canManage }: Props) {
  const [idx, setIdx] = useState(0);
  const [goingCount, setGoingCount] = useState(0);
  useEffect(() => subscribeRsvpCount(ev.id, setGoingCount), [ev.id]);

  const [rsvps, setRsvps] = useState<Array<{ uid: string; username?: string | null; displayName?: string | null; avatarUrl?: string | null }>>([]);
  useEffect(() => subscribeRsvps(ev.id, setRsvps), [ev.id]);

  // Load current user's profile (for optimistic RSVP details)
  const [meProfile, setMeProfile] = useState<any>(null);
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    const unsub = onSnapshot(doc(db, "users", u.uid), (snap) => {
      if (snap.exists()) setMeProfile(snap.data());
    });
    return unsub;
  }, []);

  const currentUid = auth.currentUser?.uid;
  const amGoing = !!currentUid && rsvps.some(r => r.uid === currentUid);

  async function handleToggleRsvp(e: React.MouseEvent) {
    e.stopPropagation();
    const u = auth.currentUser;
    if (!u) return;
    const next = !amGoing;

    // Optimistic update
    setRsvps(prev => {
      if (next) {
        if (prev.some(p => p.uid === u.uid)) return prev;
        return [
          ...prev,
            {
              uid: u.uid,
              username: meProfile?.username || meProfile?.slug || (u.email ? u.email.split("@")[0] : null),
              displayName: meProfile?.displayName || u.displayName || null,
              avatarUrl: meProfile?.avatarUrl || u.photoURL || null
            }
        ];
      } else {
        return prev.filter(p => p.uid !== u.uid);
      }
    });

    try {
      await toggleRsvp(ev.id, next);
    } catch (err) {
      console.error("RSVP toggle failed", err);
      // Revert optimistic change
      setRsvps(prev => {
        if (next) {
          return prev.filter(p => p.uid !== u.uid);
        } else {
          // Re-add if missing
            if (prev.some(p => p.uid === u.uid)) return prev;
            return [
              ...prev,
              {
                uid: u.uid,
                username: meProfile?.username || meProfile?.slug || (u.email ? u.email.split("@")[0] : null),
                displayName: meProfile?.displayName || u.displayName || null,
                avatarUrl: meProfile?.avatarUrl || u.photoURL || null
              }
            ];
        }
      });
    }
  }

  const visFallback = eventFallbackByVisibility(ev.visibility);
  const primaryFormat = Array.isArray(ev.formatTypes) && ev.formatTypes.length ? ev.formatTypes[0] : null;
  const isLgs = !!ev.organizerIsLGS || (ev as any).locationType === "LGS";
  
  // NEW: load organizer LGS profile to obtain address if not on event
  const [lgsProfile, setLgsProfile] = useState<any>(null);
  useEffect(() => {
    if (!isLgs || !ev.organizerUid) return;
    let canceled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", ev.organizerUid));
        if (!canceled && snap.exists()) setLgsProfile(snap.data());
      } catch (e) {
        console.warn("[EventCard] LGS profile load failed", e);
      }
    })();
    return () => { canceled = true; };
  }, [isLgs, ev.organizerUid]);

  // NEW: derive LGS address (prefer event address, else profile variants)
  const organizerLgsAddress = useMemo(() => {
    if (!isLgs) return null;
    const candidates: any[] = [
      ev.address,
      lgsProfile?.address,
      lgsProfile?.lgsAddress,
      lgsProfile?.storeAddress,
      lgsProfile?.locationAddress,
      lgsProfile?.streetAddress,
      lgsProfile?.location?.address,
      lgsProfile?.store?.address,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim().length > 5) return c.trim();
    }
    return null;
  }, [isLgs, ev.address, lgsProfile]);

  // ADD: robust distance detector (fixes "distanceStr is not defined" error)
  const distanceStr = useMemo(() => {
    const candidates: any[] = [
      (ev as any).distanceMiles,
      (ev as any).distance_miles,
      (ev as any).distanceMi,
      (ev as any).distance_mi,
      (ev as any).distance,
      (ev as any).dist,
      (ev as any).location?.distanceMiles,
      (ev as any).location?.distance
    ];
    for (const raw of candidates) {
      if (raw == null) continue;
      let n: number | null = null;
      if (typeof raw === "number") n = raw;
      else if (typeof raw === "string") {
        const p = parseFloat(raw);
        if (Number.isFinite(p)) n = p;
      }
      if (n != null && n >= 0) {
        return `${n.toFixed(n >= 10 ? 0 : 1)} mi`;
      }
    }
    return null;
  }, [ev]);

  // ADD: derive LGS email (was referenced but not defined in this file version)
  const lgsEmail = useMemo(() => {
    return (
      (ev as any).lgsEmail ||
      (ev as any).organizerEmail ||
      (ev as any).email ||
      (ev as any).contactEmail ||
      lgsProfile?.businessEmail ||
      lgsProfile?.storeEmail ||
      lgsProfile?.email ||
      null
    );
  }, [ev, lgsProfile]);

  // Helper: decide if LGS address differs from event address
  const showLgsAddress = useMemo(
    () => !!organizerLgsAddress && organizerLgsAddress !== ev.address,
    [organizerLgsAddress, ev.address]
  );

  // After rsvps are loaded, fetch missing profile fields (avatar/username/displayName)
  useEffect(() => {
    // Only fetch for entries missing BOTH a proper name and avatar (or explicitly flagged unhydrated)
    const lacking = rsvps.filter(u =>
      (!u.avatarUrl) ||
      ((!u.displayName && !u.username) && !(u as any)._hydrated)
    );
    if (!lacking.length) return;

    let canceled = false;
    (async () => {
      const results = await Promise.all(
        lacking.map(async u => {
          try {
            const snap = await getDoc(doc(db, "users", u.uid));
            if (!snap.exists()) return null;
            return { uid: u.uid, ...(snap.data() as any) };
          } catch {
            return null;
          }
        })
      );
      if (canceled) return;

      setRsvps(prev =>
        prev.map(p => {
          const prof = results.find(r => r && r.uid === p.uid);
          if (!prof) return p;
          // Accept multiple possible field names
          const username =
            p.username ||
            prof.username ||
            prof.slug ||
            prof.handle ||
            (prof.email ? prof.email.split("@")[0] : undefined) ||
            p.uid.slice(0, 6);

          const displayName =
            p.displayName ||
            prof.displayName ||
            prof.fullName ||
            prof.name ||
            prof.username ||
            null;

            const avatar =
              p.avatarUrl ||
              prof.avatarUrl ||
              prof.avatar_url ||
              prof.photoURL ||
              prof.photo_url ||
              prof.image ||
              prof.imageUrl ||
              null;

          return {
            ...p,
            username,
            displayName,
            avatarUrl: avatar,
            _hydrated: true
          };
        })
      );
    })();

    return () => { canceled = true; };
  }, [rsvps]);

  // Build gallery but ensure the hero image is first
  const galleryRaw = [pickHero(ev)];
  const gallery = Array.from(new Set(galleryRaw.filter(Boolean)));

  const cardCls =
    "rounded-lg border shadow-md overflow-hidden " +
    (isLgs ? "bg-indigo-950/80 border-indigo-500/20" : "bg-slate-900/70 border-white/10");

  const [shareOpen, setShareOpen] = useState(false);
  const shareHref = `/${encodeURIComponent(ev.organizerUsername || "")}?tab=events&focus=${encodeURIComponent(ev.id)}`;
  const [shareUserQuery, setShareUserQuery] = useState("");
  const [shareSearching, setShareSearching] = useState(false);
  const [shareUserResult, setShareUserResult] = useState<any | null>(null);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  // Lookup user by username (case-insensitive exact)
  async function lookupUser(uname: string) {
    const q = uname.trim().toLowerCase();
    if (!q) { setShareUserResult(null); return; }
    setShareSearching(true);
    setShareStatus(null);
    try {
      // try users where username == q
      const qq = query(collection(db, "users"), where("username", "==", q));
      const snap = await getDocs(qq);
      if (!snap.empty) {
        const doc0 = snap.docs[0];
        setShareUserResult({ id: doc0.id, ...(doc0.data() as any) });
      } else {
        setShareUserResult(null);
        setShareStatus("User not found");
      }
    } catch (e) {
      console.warn("[share] user lookup failed", e);
      setShareStatus("Lookup failed");
    } finally {
      setShareSearching(false);
    }
  }

  async function shareToFollowers() {
    if (!auth.currentUser) return;
    try {
      setShareStatus("Sharing to followers…");
      // Placeholder: write a single broadcast doc; backend fan-out can process.
      await addDoc(collection(db, "share_broadcasts"), {
        kind: "event_share_followers",
        eventId: ev.id,
        organizerUid: ev.organizerUid,
        createdAt: Date.now(),
      });
      setShareStatus("Shared to followers");
    } catch (e) {
      console.warn(e);
      setShareStatus("Failed to share");
    }
  }

  async function shareToUser() {
    if (!auth.currentUser || !shareUserResult) return;
    try {
      setShareStatus("Sending...");
      await addDoc(collection(db, "direct_shares"), {
        kind: "event_share_direct",
        eventId: ev.id,
        toUid: shareUserResult.id,
        fromUid: auth.currentUser.uid,
        createdAt: Date.now(),
      });
      setShareStatus(`Shared with @${shareUserResult.username || "user"}`);
    } catch (e) {
      console.warn(e);
      setShareStatus("Failed to send");
    }
  }

  const displayGoing = typeof (ev as any).rsvpsCount === "number" ? (ev as any).rsvpsCount : goingCount;

  return (
    <div className={cardCls}>
      {/* Title + header gradient (all header text now black) */}
      <div className="w-full px-3 py-2 bg-gradient-to-br from-amber-400 via-orange-500 to-sky-500 dark:from-amber-500 dark:via-orange-600 dark:to-sky-600 text-blue-900">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm sm:text-base md:text-lg font-extrabold truncate">
            {ev.title}
          </div>
          {/* Distance shown on the right ONLY when not an LGS (for LGS it's moved into the badge) */}
          {!isLgs && distanceStr && (
            <div className="text-[11px] sm:text-xs font-semibold opacity-90 tabular-nums shrink-0">
              {distanceStr}
            </div>
          )}
        </div>

        {/* Organizer row */}
        <div className="mt-1 flex items-center gap-2">
          <Link
            to={`/${ev.organizerUsername}`}
            className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-white/60 hover:bg-white/80 transition border border-black/10"
            title={ev.organizerDisplayName || `@${ev.organizerUsername}`}
          >
            <img
              src={fixUrl(ev.organizerAvatarUrl || undefined) || "/events/placeholder.png"}
              alt=""
              className="w-5 h-5 rounded-full object-cover ring-1 ring-black/10"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/events/placeholder.png";
              }}
            />
            <span className="text-[11px] sm:text-xs font-medium truncate max-w-[28ch]">
              {isLgs
                ? (ev.organizerDisplayName || ev.organizerUsername || "")
                : `@${ev.organizerUsername}`}
            </span>
          </Link>

          {isLgs && (
            <span
              className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] px-2 py-1 rounded-full font-semibold leading-none"
              title={distanceStr ? `Local Game Store • ${distanceStr} away` : "Local Game Store"}
            >
              <img
                src="/images/yesLGS.png"
                alt="LGS"
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.display = "none";
                }}
              />
              <span className="tracking-wide">
                LGS{distanceStr ? ` • ${distanceStr}` : ""}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Single hero image (rules-based) */}
      <div className="relative aspect-[16/9] bg-black/30">
        <img
          src={gallery[0] || visFallback}
          alt=""
          className="w-full h-full object-contain object-center"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = visFallback;
          }}
        />
      </div>

      {/* Type / location / addresses row (moved LGS address here to left side) */}
      <div className="px-3 py-2 text-xs text-indigo-100 bg-violet-950/40 border-b border-violet-400/20 flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-x-3 gap-y-1 min-w-0">
          {/** LGS Address (or unified address) FIRST */}
          {organizerLgsAddress && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <span className="opacity-80">
                {showLgsAddress ? "LGS:" : "Address:"}
              </span>
              <a
                href={googleMapsHref(organizerLgsAddress, ev.location || null)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium truncate max-w-[38ch] underline"
                onClick={(e) => e.stopPropagation()}
                title="Open in Google Maps"
              >
                {organizerLgsAddress}
              </a>
            </span>
          )}

          {/** Event address (only if different from LGS address) */}
          {ev.address && showLgsAddress && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <span className="opacity-80">Event:</span>
              <a
                href={googleMapsHref(ev.address, ev.location || null)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium truncate max-w-[38ch] underline"
                onClick={(e) => e.stopPropagation()}
                title="Open in Google Maps"
              >
                {ev.address}
              </a>
            </span>
          )}

          {ev.eventType && (
            <span className="inline-flex items-center gap-1">
              <span className="opacity-80">Type:</span>
              <span className="font-medium truncate max-w-[32ch]">{ev.eventType}</span>
            </span>
          )}
          {ev.locationType && (
            <span className="inline-flex items-center gap-1">
              <span className="opacity-80">Location:</span>
              <span className="font-medium">{ev.locationType}</span>
            </span>
          )}
        </div>
        {primaryFormat && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/10 text-indigo-100 border border-white/15 text-[11px]">
            {primaryFormat}
          </span>
        )}
      </div>

      <CardContent className="p-3 space-y-3">
        {/* Meta grid (with LGS address row) */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-indigo-200">
          <div>
            <span className="opacity-80">Visibility:</span>{" "}
            <span className="text-indigo-100 font-medium">{ev.visibility}</span>
          </div>
          <div>
            <span className="opacity-80">Starts:</span>{" "}
            <span className="text-indigo-100 font-medium">
              {safeFormat(ev.startTime, "EEE, MMM d • h:mma")}
            </span>
          </div>
          {ev.endTime && (
            <div>
              <span className="opacity-80">Ends:</span>{" "}
              <span className="text-indigo-100 font-medium">
                {safeFormat(ev.endTime, "EEE, MMM d • h:mma", "")}
              </span>
            </div>
          )}
          {distanceStr && (
            <div>
              <span className="opacity-80">Distance:</span>{" "}
              <span className="text-indigo-100 font-medium">{distanceStr}</span>
            </div>
          )}
          {/** REMOVED previous separate LGS Address row (now in top type row) */}
        </div>

        {/* Adjusted conditional block: remove organizerLgsAddress duplication */}
        {( (ev.address && showLgsAddress) || (isLgs && lgsEmail)) && (
          <div className="rounded-md border border-white/10 bg-white/5 p-3 text-xs space-y-2">
            {ev.address && showLgsAddress && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="opacity-70">Event Address:</span>
                <a
                  href={googleMapsHref(ev.address, ev.location || null)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white truncate max-w-full"
                  onClick={(e) => e.stopPropagation()}
                  title="Open in Google Maps"
                >
                  {ev.address}
                </a>
              </div>
            )}
            {isLgs && lgsEmail && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="opacity-70">LGS Email:</span>
                <a
                  href={`mailto:${lgsEmail}`}
                  className="underline hover:text-white break-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  {lgsEmail}
                </a>
              </div>
            )}
          </div>
        )}

        {ev.details && (
          <div className="rounded-lg bg-black/20 border border-white/10 p-3 text-sm text-slate-100 whitespace-pre-wrap">
            {ev.details}
          </div>
        )}

        {/* RSVPs row */}
        <div className="pt-1">
          <div className="text-xs text-indigo-200 mb-1">
            {displayGoing > 0 ? `${displayGoing} going` : "Be the first to RSVP"}
          </div>
          {rsvps.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {rsvps.map((u) => (
                <span key={u.uid} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-white/5 border border-white/10">
                  {u.avatarUrl ? (
                    <img
                      src={fixUrl(u.avatarUrl || undefined) || "/events/placeholder.png"}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/events/placeholder.png";
                      }}
                    />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-indigo-600/40 grid place-items-center text-[10px] text-white/90">
                      {(u.displayName || u.username || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span className="text-xs text-indigo-100 max-w-[18ch] truncate">
                    {u.displayName || u.username || "User"}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2 flex-wrap">
            {/** Manage buttons for owner */}
            {auth.currentUser?.uid === ev.organizerUid && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEdit?.(ev); }}
                  className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-indigo-400/30 text-indigo-200 bg-indigo-600/15 hover:bg-indigo-600/25"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!onDelete) return;
                    if (!confirm("Delete this event? This cannot be undone.")) return;
                    onDelete(ev);
                  }}
                  className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-500/30 text-rose-200 bg-rose-600/15 hover:bg-rose-600/25"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}

            {/* Favorite (Heart) */}
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await toggleFavorite(ev.id, !!isFavorite);
                } catch (err) {
                  console.error("Favorite toggle error", err);
                }
              }}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-white/15 bg-white/5 hover:bg-white/10"
            >
              <Heart className={`w-4 h-4 ${isFavorite ? "fill-red-500 text-red-500" : "text-red-400"}`} />
              <span>{isFavorite ? "Favorited" : "Favorite"}</span>
            </button>

            {/* Share */}
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setShareOpen(true);
              }}
              aria-label="Share event"
              className="flex items-center gap-1"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>

            {/* Single RSVP toggle */}
            <button
              type="button"
              onClick={handleToggleRsvp}
              className={`text-xs inline-flex items-center gap-2 px-3 py-1 rounded-md border transition
                ${amGoing
                  ? "bg-green-600/80 border-green-400/40 text-white hover:bg-green-600"
                  : "bg-white/5 border-white/15 text-indigo-100 hover:bg-white/10"}`}
            >
              {amGoing ? "RSVP'd" : "RSVP"}
            </button>
          </div>
          <div className="text-xs text-indigo-200 flex items-center gap-3">
            {typeof ev.favoritesCount === "number" && ev.favoritesCount > 0 ? (
              <span>{ev.favoritesCount} starred</span>
            ) : (
              <span />
            )}
            <span>{displayGoing} going</span>
          </div>
        </div>
        {/* Share dialog */}
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share event</DialogTitle>
              <DialogDescription>
                Copy link or share directly.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3">
              <div>
                <label className="text-xs font-medium text-indigo-200">Link</label>
                <Input
                  readOnly
                  value={`${location.origin}${shareHref}`}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${location.origin}${shareHref}`);
                        setShareStatus("Link copied");
                      } catch {
                        setShareStatus("Copy failed");
                      }
                    }}
                  >
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const url = `${location.origin}${shareHref}`;
                      if ((navigator as any).share) {
                        try { await (navigator as any).share({ title: ev.title, url }); } catch {}
                      } else {
                        window.open(url, "_blank");
                      }
                    }}
                  >
                    Open
                  </Button>
                </div>
              </div>

              {auth.currentUser?.uid === ev.organizerUid && (
                <div className="space-y-4 border-t border-white/10 pt-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={shareToFollowers}
                      className="inline-flex items-center gap-1"
                    >
                      <Users className="w-4 h-4" />
                      Share to followers
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-medium text-indigo-200">Share to user (username)</div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="@username"
                        value={shareUserQuery}
                        onChange={(e) => setShareUserQuery(e.target.value)}
                        onBlur={() => lookupUser(shareUserQuery.replace(/^@/, ""))}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={shareSearching || !shareUserQuery.trim()}
                        onClick={() => lookupUser(shareUserQuery.replace(/^@/, ""))}
                      >
                        {shareSearching ? "…" : "Find"}
                      </Button>
                      <Button
                        size="sm"
                        disabled={!shareUserResult}
                        onClick={shareToUser}
                        className="inline-flex items-center gap-1"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </Button>
                    </div>
                    {shareUserResult && (
                      <div className="text-[11px] text-indigo-200">
                        Found: @{shareUserResult.username || shareUserResult.id.slice(0, 6)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shareStatus && (
                <div className="text-[11px] text-indigo-300">{shareStatus}</div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="ghost" onClick={() => setShareOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </div>
  );
}

